import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, UserPlus, LogOut, Trash2, Crown } from 'lucide-react';
import type { PodDetail } from '../../services/podsApi';
import './PodDetailSheet.css';

interface PodDetailSheetProps {
  pod: PodDetail;
  onClose: () => void;
  onInvite: (email: string) => Promise<void>;
  onLeave: () => Promise<void>;
  onRemoveMember: (userId: number) => Promise<void>;
  onRename: (name: string) => Promise<void>;
  onDelete: () => Promise<void>;
  isLoading?: boolean;
}

const PodDetailSheet: React.FC<PodDetailSheetProps> = ({
  pod, onClose, onInvite, onLeave, onRemoveMember, onRename, onDelete, isLoading,
}) => {
  const navigate = useNavigate();
  const [inviteEmail, setInviteEmail] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(pod.name);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleInvite = async () => {
    if (!inviteEmail.includes('@')) return;
    await onInvite(inviteEmail.trim().toLowerCase());
    setInviteEmail('');
    setShowInvite(false);
  };

  const handleRename = async () => {
    const trimmed = nameInput.trim();
    if (trimmed && trimmed !== pod.name) {
      await onRename(trimmed);
    }
    setEditingName(false);
  };

  return (
    <div className="pdsheet-overlay" onClick={onClose}>
      <div className="pdsheet" onClick={e => e.stopPropagation()}>
        <div className="pdsheet-handle" />

        {/* Header */}
        <div className="pdsheet-header">
          {editingName ? (
            <input
              className="pdsheet-name-input"
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              onBlur={handleRename}
              onKeyDown={e => e.key === 'Enter' && handleRename()}
              maxLength={50}
              autoFocus
            />
          ) : (
            <h2 className="pdsheet-name" onClick={() => setEditingName(true)}>{pod.name}</h2>
          )}
          <button className="pdsheet-close" onClick={onClose}><X size={18} /></button>
        </div>

        {isLoading && <div className="pdsheet-loading">Loading...</div>}

        {/* Members — only shown for active members (full detail) */}
        {pod.members && <div className="pdsheet-section">
          <div className="pdsheet-section-header">
            <span className="pdsheet-section-label">Members</span>
            <button className="pdsheet-action-btn" onClick={() => setShowInvite(!showInvite)}>
              <UserPlus size={14} /> Add
            </button>
          </div>

          {showInvite && (
            <div className="pdsheet-invite-row">
              <input
                className="pdsheet-invite-input"
                type="email"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                placeholder="Email address"
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleInvite())}
                autoFocus
              />
              <button className="pdsheet-invite-send" onClick={handleInvite} disabled={!inviteEmail.includes('@')}>
                Invite
              </button>
            </div>
          )}

          <div className="pdsheet-members">
            {(pod.members || []).map(m => (
              <div key={m.id} className="pdsheet-member">
                <span className="pdsheet-member-avatar">
                  {m.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                </span>
                <div className="pdsheet-member-info">
                  <span className="pdsheet-member-name">
                    {m.name}
                    {pod.admin_user_id != null && Number(m.id) === pod.admin_user_id && (
                      <Crown size={10} className="pdsheet-crown" />
                    )}
                  </span>
                  {m.status === 'pending' && <span className="pdsheet-member-badge">Pending</span>}
                </div>
                {pod.is_creator && Number(m.id) !== pod.admin_user_id && m.status !== 'pending' && (
                  <button
                    className="pdsheet-member-remove"
                    onClick={() => onRemoveMember(Number(m.id))}
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>}

        {/* Plans — only shown for active members */}
        {pod.plans && pod.plans.length > 0 && (
          <div className="pdsheet-section">
            <span className="pdsheet-section-label">Plans</span>
            <div className="pdsheet-plans">
              {pod.plans.map(p => (
                <div
                  key={p.plan_id}
                  className="pdsheet-plan"
                  onClick={() => { onClose(); navigate(`/itinerary/${p.plan_id}`); }}
                >
                  <div className="pdsheet-plan-info">
                    <span className="pdsheet-plan-title">{p.title}</span>
                    {p.destination && <span className="pdsheet-plan-dest">{p.destination}</span>}
                  </div>
                  <span className={`pdsheet-plan-state pdsheet-plan-state--${p.lifecycle_state}`}>
                    {p.lifecycle_state}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats — only for active members */}
        {pod.stats && (
          <div className="pdsheet-stats">
            <div className="pdsheet-stat">
              <span className="pdsheet-stat-num">{pod.stats.total_plans}</span>
              <span className="pdsheet-stat-label">plans</span>
            </div>
            <div className="pdsheet-stat">
              <span className="pdsheet-stat-num">{pod.stats.total_days_shared}</span>
              <span className="pdsheet-stat-label">days shared</span>
            </div>
            <div className="pdsheet-stat">
              <span className="pdsheet-stat-num">{pod.member_count}</span>
              <span className="pdsheet-stat-label">members</span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="pdsheet-footer-actions">
          <button className="pdsheet-leave" onClick={onLeave}>
            <LogOut size={14} /> Leave Pod
          </button>
          {pod.is_creator && (
            confirmDelete ? (
              <div className="pdsheet-delete-confirm">
                <span>Delete this Pod?</span>
                <button className="pdsheet-delete-yes" onClick={onDelete}>Delete</button>
                <button className="pdsheet-delete-no" onClick={() => setConfirmDelete(false)}>Cancel</button>
              </div>
            ) : (
              <button className="pdsheet-delete" onClick={() => setConfirmDelete(true)}>
                <Trash2 size={14} /> Delete Pod
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default PodDetailSheet;
